import { useNavigation } from "react-router";
import { useSpinDelay } from "spin-delay";

import { Progress } from "./progress";

/**
 * The route-change progress bar for a soft navigation.
 *
 * WHY THIS IS SHARED, AND WHY THE DELAY IS 150ms
 *
 * This used to live only inside `MeganavProgress`, which is rendered only by
 * `meganav.tsx` — and `Meganav` is only mounted by the CHECKOUT layout. The
 * storefront layout renders `HecHeader`, which had no indicator at all. So
 * every PDP, PLP and content page navigated with **zero** feedback.
 *
 * That is what shoppers reported as "clicking a product does nothing, then the
 * page jumps": React Router keeps the previous page fully rendered and
 * interactive while the next loader runs, so with no pending UI the only
 * observable event is the content swapping all at once at the end.
 *
 * The wait is not short. Measured on staging, a PDP -> PDP soft navigation
 * fetches ~374KB and takes 0.85-1.63s. The old `delay: 600` was tuned to
 * suppress the bar on fast transitions, but at ~1s of real loader time it hid
 * the indicator for most of the wait — the majority of the "nothing is
 * happening" window. 150ms still suppresses flicker on genuinely instant
 * (cached / prefetched) navigations while covering the visible part of a slow
 * one.
 *
 * `minDuration: 200` is unchanged: once the bar appears it stays long enough to
 * read as intentional rather than as a flash.
 *
 * Deliberately NOT a new visual language — same `Progress` primitive, same 2px
 * bar. The only changes are where it renders and when it starts.
 */
export const RouteChangeProgress = ({
  className = "absolute bottom-0 left-0 right-0 h-0.5 overflow-hidden",
}: {
  className?: string;
}) => {
  const navigation = useNavigation();
  const isLoading = useSpinDelay(
    ["submitting", "loading"].includes(navigation.state),
    { delay: 150, minDuration: 200 },
  );

  return (
    <div className={className} aria-hidden="true">
      <div className="relative">
        <Progress isAnimating={isLoading} />
      </div>
    </div>
  );
};
